/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, Suspense, useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap, jsx } from '@atlaskit/css';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { SainAction, useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { AISources } from '../../common/ui/ai-sources';
import { extractDate } from '../../common/utils/extract-date';
import { extractLocation } from '../../common/utils/extract-location';
import { QueryIntent } from '../../constants';
import { DateCard } from '../../v2/knowledge-cards/feature-cards/DateCard';
import { LocationCard } from '../../v2/knowledge-cards/feature-cards/LocationCard';
import { AIAnswerBody } from '../ai-answer-body/ai-answer-body';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerContentWarningAndBrandSection, SearchPageSmartAnswerFooter } from '../ai-answer-footer/ai-answer-footer';
import { AIAnswerLoadingStream } from '../ai-answer-loading/ai-answer-loading-stream';
import GeneratingAnimation from '../ai-answer-loading/generating-animation';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
	aiAnswerExpandShown,
	getSmartLinksAnalyticsContextData,
	onAIAnswerLoadingDialogShown,
	onAIAnswerSourceClick,
	SmartLinksAnalyticsContextLocation,
} from '../analytics';
import { NLPSearchResultFormat, type NLPSearchResultSource } from '../types';

import { Collapse } from './collapse';
import { messages } from './messages';
import { AIAnswerSearchBorder, GeneratingContainer, GeneratingText } from './styled';

const styles = cssMap({
	spacer: {
		height: '14px',
		paddingTop: token('space.250'),
	},
	errorContainer: {
		paddingTop: token('space.100'),
	},
	sourcesContainer: {
		paddingTop: '0',
		paddingRight: token('space.300'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.300'),
	},
});

const MAX_SOURCES = 4;

type SmartAnswerLoadingDialogProps = {
	collapsed: boolean;
	toggleCollapsedState?: () => void;
	callbacks?: {
		// called when card is rendered in the expanded state due to the answer being short
		onExpandedByDefault?: () => void;
		onExpandClicked?: () => void;
		onCollapseClicked?: () => void;
	};
};

const SmartAnswerLoadingDialog = ({
	collapsed,
	toggleCollapsedState,
	callbacks,
}: SmartAnswerLoadingDialogProps) => {
	const { commonAttributes, partialResponse, queryReasoning, smartAnswersRoute } =
		useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { serveExperiment: serveSAINReasoningExperiment } = getFrontendExperiment({
		abTestId: 'kd_sain_reasoning_exp',
	});

	// no need for the Collapse component when showing the ... generating animation
	if (
		!partialResponse ||
		partialResponse?.format !== NLPSearchResultFormat.MARKDOWN ||
		smartAnswersRoute === QueryIntent.DATE ||
		smartAnswersRoute === QueryIntent.LOCATION
	) {
		return (
			<AIAnswerSearchBorder isLoading isStreaming={false}>
				<GeneratingContainer>
					<GeneratingAnimation />
					<GeneratingText>
						{serveSAINReasoningExperiment && queryReasoning ? (
							queryReasoning
						) : (
							<FormattedMessage {...messages.ai_answer_generating_rovo} />
						)}
					</GeneratingText>
				</GeneratingContainer>
				<AIAnswerContentWarningAndBrandSection />
			</AIAnswerSearchBorder>
		);
	}
	return (
		<AIAnswerSearchBorder isLoading isStreaming>
			<Suspense fallback={<Fragment />}>
				<AnalyticsContext
					data={getSmartLinksAnalyticsContextData({
						location: SmartLinksAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
						commonAttributes,
					})}
				>
					<SmartCardProvider>
						<Collapse
							collapsed={collapsed}
							isLoading
							isStreaming
							expandWhenClickedAnywhere
							toggleCollapsedState={toggleCollapsedState}
							callbacks={callbacks}
						>
							<AIAnswerLoadingStream markdown={partialResponse.partialNlpResult} />
						</Collapse>
						<AIAnswerContentWarningAndBrandSection />
					</SmartCardProvider>
				</AnalyticsContext>
			</Suspense>
		</AIAnswerSearchBorder>
	);
};

type SmartAnswerCardProps = {
	onFMPReached?: () => void;
	onAnswerExpandedChange?: (expanded: boolean) => void;
};

export const SmartAnswerCard: ({ onFMPReached, onAnswerExpandedChange }: SmartAnswerCardProps) => JSX.Element = ({ onFMPReached, onAnswerExpandedChange }: SmartAnswerCardProps) => {
	const {
		isAnswered,
		hasError,
		commonAttributes,
		callbacks,
		smartAnswersRoute,
		loading,
		initiallyExpanded,
	} = useAIAnswerContext();
	const { answerFormat, answerString, query } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [collapsed, setCollapsed] = useState(false);

	// reset on each new query
	useEffect(() => {
		setCollapsed(!initiallyExpanded);
		// Notify parent context of initial state
		onAnswerExpandedChange?.(!initiallyExpanded);
	}, [query, initiallyExpanded, onAnswerExpandedChange]);

	// Call onFMPReached when answer is loaded
	useEffect(() => {
		if (!loading && !hasError) {
			onFMPReached?.();
		}
	}, [loading, hasError, onFMPReached]);

	const toggleCollapsedState = () => {
		setCollapsed(!collapsed);
		// Notify parent context of state change
		onAnswerExpandedChange?.(!collapsed);
	};

	const analyticsCallbacks = {
		onExpandedByDefault: () => fireAnalyticsEvent(aiAnswerExpandShown(commonAttributes)),
		onExpandClicked: () => {
			fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
			fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
		},
		onCollapseClicked: () => fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes)),
	};

	const sourceClickCallback = useCallback(
		(index: number, source: NLPSearchResultSource, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					sourceProduct: source.product || '',
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct: source.product || '',
			});
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks],
	);

	if (loading) {
		return (
			<SmartAnswerLoadingDialog
				collapsed={collapsed}
				toggleCollapsedState={toggleCollapsedState}
				callbacks={analyticsCallbacks}
			/>
		);
	}

	let answerContent = (
		<Collapse
			collapsed={collapsed}
			toggleCollapsedState={toggleCollapsedState}
			expandWhenClickedAnywhere
			isLoading={false}
			callbacks={analyticsCallbacks}
		>
			<AIAnswerBody />
		</Collapse>
	);

	if (hasError) {
		answerContent = (
			<Box xcss={styles.errorContainer}>
				<AIAnswerBody />
			</Box>
		);
	} else if (answerFormat === NLPSearchResultFormat.JSON) {
		answerContent = (
			<Fragment>
				<Box xcss={styles.spacer}></Box>
				<AIAnswerBody />
			</Fragment>
		);
	}

	const dateInAnswer = extractDate(answerString);
	if (isAnswered && smartAnswersRoute === QueryIntent.DATE && dateInAnswer) {
		return (
			<Fragment>
				<AIAnswerSearchBorder isLoading={false} isStreaming={false}>
					<DateCard date={dateInAnswer} />
				</AIAnswerSearchBorder>
			</Fragment>
		);
	}

	const locationInAnswer = extractLocation(answerString);
	if (isAnswered && smartAnswersRoute === QueryIntent.LOCATION && locationInAnswer) {
		return (
			<Fragment>
				<AIAnswerSearchBorder isLoading={false} isStreaming={false}>
					<LocationCard location={locationInAnswer} />
				</AIAnswerSearchBorder>
			</Fragment>
		);
	}

	return (
		<AIAnswerSearchBorder isLoading={false} isStreaming={false}>
			{answerContent}
			{!hasError && (
				<Box xcss={styles.sourcesContainer} testId="ai-answer-sources">
					<AISources
						sources={commonAttributes.sources}
						searchSessionId={commonAttributes.searchSessionId}
						onClickHandler={sourceClickCallback}
						allowFreeflowLayout={true}
						limit={MAX_SOURCES}
					/>
				</Box>
			)}
			<SearchPageSmartAnswerFooter />
		</AIAnswerSearchBorder>
	);
};
